import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  FiBriefcase, FiPlus, FiEdit2, FiTrash2, FiCheck, FiX,
  FiSearch, FiUsers, FiPhone, FiMail, FiMapPin, FiHash,
  FiStar, FiUserPlus, FiChevronDown, FiChevronUp,
  FiRefreshCw, FiAlertCircle, FiCheckCircle, FiLoader, FiImage
} from 'react-icons/fi';
import { useAuth } from '../../context/AuthContext';
import { companyAPI } from '../../services/api';
import { toast } from 'react-toastify';
import CompanySignatures from './CompanySignatures';

// ─── Theme constants ────────────────────────────────────────────────────────────
const COLORS = {
  primary: '#059669',
  primaryHover: '#047857',
  dark: '#064e3b',
  light: '#ecfdf5',
  lightHover: '#d1fae5',
  accent: '#10b981',
  accentGlow: 'rgba(16, 185, 129, 0.3)',
  white: '#ffffff',
  textPrimary: '#111827',
  textSecondary: '#6b7280',
  textMuted: '#9ca3af',
  border: 'rgba(16, 185, 129, 0.2)',
  borderFocus: '#10b981',
  danger: '#ef4444',
  dangerBg: 'rgba(239, 68, 68, 0.08)',
  dangerBorder: 'rgba(239, 68, 68, 0.2)',
  glass: 'rgba(255, 255, 255, 0.75)',
  glassBorder: 'rgba(255, 255, 255, 0.5)',
  overlayBg: 'rgba(6, 78, 59, 0.6)',
};

// ─── Utility: format date to DD.MM.YYYY ─────────────────────────────────────────
const formatDate = (dateStr) => {
  if (!dateStr) return '--';
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return '--';
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const yyyy = d.getFullYear();
  return `${dd}.${mm}.${yyyy}`;
};

// ─── Validation helpers ─────────────────────────────────────────────────────────
const validateIEC = (iec) => /^[A-Za-z0-9]{10}$/.test(iec);
const validateGSTIN = (gstin) => /^[A-Za-z0-9]{15}$/.test(gstin);

// ─── Signing authority type options ─────────────────────────────────────────────
const SIGNING_AUTH_TYPES = ['Director', 'Proprietor', 'Partner'];

// ─── Empty company form ─────────────────────────────────────────────────────────
const EMPTY_COMPANY = {
  iec: '',
  exporter_name: '',
  exporter_address: '',
  gstin: '',
  email: '',
  phone: '',
  negotiation_bank: '',
  bank_account_no: '',
  goods_carried_by: '',
  iba_code: '',
  icc_bcc_code: '',
  registration_date: '',
  signing_auth_type: 'Director',
  invoice_prefix: 'INV-',
  invoice_starting_number: 1,
  proforma_invoice_prefix: 'PI-',
  proforma_invoice_starting_number: 1,
  advising_bank: '',
  terms_and_conditions: '',
};

const EMPTY_AUTHORITY = {
  authority_name: '',
  authority_type: '',
  designation: '',
  is_primary: false,
};

// ─── Shared inline styles ───────────────────────────────────────────────────────
const styles = {
  page: {
    minHeight: '100vh',
    padding: '32px 40px',
    background: `linear-gradient(145deg, ${COLORS.light} 0%, #d1fae5 50%, ${COLORS.light} 100%)`,
    fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 32,
    flexWrap: 'wrap',
    gap: 16,
  },
  title: {
    fontFamily: 'Poppins, sans-serif',
    fontSize: 28,
    fontWeight: 800,
    color: COLORS.dark,
    margin: 0,
  },
  subtitle: {
    fontSize: 14,
    color: COLORS.textSecondary,
    marginTop: 4,
  },
  primaryBtn: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
    padding: '12px 24px',
    background: `linear-gradient(135deg, ${COLORS.primary}, ${COLORS.accent})`,
    color: COLORS.white,
    border: 'none',
    borderRadius: 12,
    fontSize: 14,
    fontWeight: 700,
    cursor: 'pointer',
    boxShadow: `0 4px 15px ${COLORS.accentGlow}`,
    fontFamily: 'Poppins, sans-serif',
  },
  searchWrapper: {
    position: 'relative',
    maxWidth: 400,
    width: '100%',
    marginBottom: 24,
  },
  searchIcon: {
    position: 'absolute',
    left: 14,
    top: '50%',
    transform: 'translateY(-50%)',
    color: COLORS.primary,
    pointerEvents: 'none',
  },
  searchInput: {
    width: '100%',
    padding: '12px 14px 12px 42px',
    border: `2px solid ${COLORS.border}`,
    borderRadius: 12,
    fontSize: 14,
    outline: 'none',
    background: COLORS.white,
    fontFamily: 'Inter, sans-serif',
    transition: 'all 0.2s ease',
    boxSizing: 'border-box',
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(400px, 1fr))',
    gap: 24,
  },
  glassCard: {
    background: COLORS.glass,
    backdropFilter: 'blur(20px)',
    WebkitBackdropFilter: 'blur(20px)',
    border: `1px solid ${COLORS.glassBorder}`,
    borderRadius: 20,
    padding: 28,
    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.06), 0 2px 8px rgba(0, 0, 0, 0.04)',
    position: 'relative',
    overflow: 'hidden',
    transition: 'transform 0.2s ease, box-shadow 0.2s ease',
  },
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: COLORS.overlayBg,
    backdropFilter: 'blur(8px)',
    WebkitBackdropFilter: 'blur(8px)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
    padding: 20,
  },
  modal: {
    background: COLORS.white,
    borderRadius: 24,
    padding: '32px',
    width: '100%',
    maxWidth: 720,
    maxHeight: '90vh',
    overflowY: 'auto',
    boxShadow: '0 25px 80px rgba(0, 0, 0, 0.25)',
    position: 'relative',
  },
  modalTitle: {
    fontFamily: 'Poppins, sans-serif',
    fontSize: 22,
    fontWeight: 700,
    color: COLORS.dark,
    margin: '0 0 24px 0',
  },
  formGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: 16,
  },
  fieldGroup: {
    marginBottom: 0,
  },
  label: {
    display: 'block',
    fontSize: 12,
    fontWeight: 600,
    color: COLORS.primaryHover,
    marginBottom: 6,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  input: {
    width: '100%',
    padding: '11px 14px',
    border: `2px solid ${COLORS.border}`,
    borderRadius: 10,
    fontSize: 14,
    outline: 'none',
    transition: 'all 0.2s ease',
    background: '#f0fdf4',
    fontFamily: 'Inter, sans-serif',
    boxSizing: 'border-box',
  },
  select: {
    width: '100%',
    padding: '11px 14px',
    border: `2px solid ${COLORS.border}`,
    borderRadius: 10,
    fontSize: 14,
    outline: 'none',
    transition: 'all 0.2s ease',
    background: '#f0fdf4',
    fontFamily: 'Inter, sans-serif',
    boxSizing: 'border-box',
    cursor: 'pointer',
    appearance: 'auto',
  },
  textarea: {
    width: '100%',
    padding: '11px 14px',
    border: `2px solid ${COLORS.border}`,
    borderRadius: 10,
    fontSize: 14,
    outline: 'none',
    transition: 'all 0.2s ease',
    background: '#f0fdf4',
    fontFamily: 'Inter, sans-serif',
    resize: 'vertical',
    minHeight: 72,
    boxSizing: 'border-box',
  },
  errorText: {
    fontSize: 11,
    color: COLORS.danger,
    marginTop: 4,
  },
  btnRow: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: 12,
    marginTop: 24,
    paddingTop: 20,
    borderTop: `1px solid ${COLORS.border}`,
  },
  cancelBtn: {
    padding: '11px 24px',
    background: 'transparent',
    color: COLORS.textSecondary,
    border: `2px solid ${COLORS.border}`,
    borderRadius: 10,
    fontSize: 14,
    fontWeight: 600,
    cursor: 'pointer',
    fontFamily: 'Poppins, sans-serif',
  },
  saveBtn: {
    padding: '11px 28px',
    background: `linear-gradient(135deg, ${COLORS.primary}, ${COLORS.accent})`,
    color: COLORS.white,
    border: 'none',
    borderRadius: 10,
    fontSize: 14,
    fontWeight: 700,
    cursor: 'pointer',
    boxShadow: `0 4px 15px ${COLORS.accentGlow}`,
    fontFamily: 'Poppins, sans-serif',
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
  },
  dangerBtn: {
    padding: '8px 16px',
    background: COLORS.dangerBg,
    color: COLORS.danger,
    border: `1px solid ${COLORS.dangerBorder}`,
    borderRadius: 8,
    fontSize: 13,
    fontWeight: 600,
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
  },
  iconBtn: {
    width: 36,
    height: 36,
    borderRadius: 10,
    border: 'none',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
  },
  badge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
    padding: '3px 10px',
    borderRadius: 20,
    fontSize: 11,
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  activeBadge: {
    background: 'rgba(16, 185, 129, 0.15)',
    color: COLORS.primary,
    border: `1px solid rgba(16, 185, 129, 0.3)`,
  },
  infoRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    fontSize: 13,
    color: COLORS.textSecondary,
    marginBottom: 6,
  },
  divider: {
    height: 1,
    background: COLORS.border,
    margin: '16px 0',
    border: 'none',
  },
  sectionTitle: {
    fontSize: 14,
    fontWeight: 700,
    color: COLORS.dark,
    marginBottom: 12,
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },
  authorityCard: {
    background: COLORS.light,
    border: `1px solid ${COLORS.border}`,
    borderRadius: 12,
    padding: '12px 16px',
    marginBottom: 8,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  spinner: {
    width: 20,
    height: 20,
    border: '2px solid rgba(255,255,255,0.3)',
    borderTopColor: COLORS.white,
    borderRadius: '50%',
    animation: 'spin 0.8s linear infinite',
  },
  emptyState: {
    textAlign: 'center',
    padding: '60px 20px',
    color: COLORS.textMuted,
  },
  fullSpan: {
    gridColumn: '1 / -1',
  },
  checkbox: {
    width: 18,
    height: 18,
    accentColor: COLORS.primary,
    cursor: 'pointer',
  },
};

// ─── Keyframes injected once ────────────────────────────────────────────────────
(() => {
  if (typeof document === 'undefined') return;
  const id = 'company-mgmt-keyframes';
  if (document.getElementById(id)) return;
  const style = document.createElement('style');
  style.id = id;
  style.textContent = `
    @keyframes spin { to { transform: rotate(360deg); } }
    @keyframes pulse-ring {
      0% { box-shadow: 0 0 0 0 rgba(16,185,129,0.4); }
      70% { box-shadow: 0 0 0 8px rgba(16,185,129,0); }
      100% { box-shadow: 0 0 0 0 rgba(16,185,129,0); }
    }

    /* ── Responsive styles ── */
    @media (max-width: 1024px) {
      .cm-page { padding: 20px 20px !important; }
      .cm-title { font-size: 22px !important; }
      .cm-grid { grid-template-columns: 1fr !important; }
    }

    @media (max-width: 768px) {
      .cm-page { padding: 12px !important; }
      .cm-title { font-size: 20px !important; }
      .cm-subtitle { font-size: 12px !important; }
      .cm-header { margin-bottom: 20px !important; gap: 12px !important; }
      .cm-grid { grid-template-columns: 1fr !important; gap: 16px !important; }
      .cm-card { padding: 18px !important; border-radius: 14px !important; }
      .cm-modal { padding: 20px !important; border-radius: 16px !important; max-width: 100% !important; margin: 8px !important; }
      .cm-modal-title { font-size: 18px !important; margin-bottom: 16px !important; }
      .cm-form-grid { grid-template-columns: 1fr !important; gap: 12px !important; }
      .cm-btn-row { flex-direction: column-reverse !important; gap: 8px !important; }
      .cm-btn-row button { width: 100% !important; justify-content: center !important; }
      .cm-overlay { padding: 8px !important; }
      .cm-search { max-width: 100% !important; }
      .cm-primary-btn { padding: 10px 18px !important; font-size: 13px !important; width: 100% !important; justify-content: center !important; }
      .cm-authority-card { flex-direction: column !important; align-items: flex-start !important; gap: 8px !important; }
      .cm-authority-actions { align-self: flex-end !important; }
    }

    @media (max-width: 480px) {
      .cm-page { padding: 8px !important; }
      .cm-title { font-size: 18px !important; }
      .cm-card { padding: 14px !important; border-radius: 12px !important; }
      .cm-card-header { flex-direction: column !important; align-items: flex-start !important; gap: 12px !important; }
      .cm-card-actions { align-self: flex-end !important; }
      .cm-modal { padding: 16px !important; border-radius: 14px !important; }
      .cm-info-row { font-size: 12px !important; }
    }
  `;
  document.head.appendChild(style);
})();

// ─── Reusable input with focus styling ──────────────────────────────────────────
function StyledInput({ error, style: extraStyle, ...props }) {
  const [focused, setFocused] = useState(false);
  return (
    <input
      {...props}
      style={{
        ...styles.input,
        ...(focused ? { borderColor: COLORS.borderFocus, boxShadow: `0 0 0 3px rgba(16,185,129,0.15)` } : {}),
        ...(error ? { borderColor: COLORS.danger, boxShadow: error && focused ? `0 0 0 3px rgba(239,68,68,0.12)` : 'none' } : {}),
        ...extraStyle,
      }}
      onFocus={(e) => { setFocused(true); props.onFocus?.(e); }}
      onBlur={(e) => { setFocused(false); props.onBlur?.(e); }}
    />
  );
}

function StyledTextarea({ error, style: extraStyle, ...props }) {
  const [focused, setFocused] = useState(false);
  return (
    <textarea
      {...props}
      style={{
        ...styles.textarea,
        ...(focused ? { borderColor: COLORS.borderFocus, boxShadow: `0 0 0 3px rgba(16,185,129,0.15)` } : {}),
        ...(error ? { borderColor: COLORS.danger } : {}),
        ...extraStyle,
      }}
      onFocus={(e) => { setFocused(true); props.onFocus?.(e); }}
      onBlur={(e) => { setFocused(false); props.onBlur?.(e); }}
    />
  );
}

function StyledSelect({ error, style: extraStyle, children, ...props }) {
  const [focused, setFocused] = useState(false);
  return (
    <select
      {...props}
      style={{
        ...styles.select,
        ...(focused ? { borderColor: COLORS.borderFocus, boxShadow: `0 0 0 3px rgba(16,185,129,0.15)` } : {}),
        ...(error ? { borderColor: COLORS.danger } : {}),
        ...extraStyle,
      }}
      onFocus={(e) => { setFocused(true); props.onFocus?.(e); }}
      onBlur={(e) => { setFocused(false); props.onBlur?.(e); }}
    >
      {children}
    </select>
  );
}

// ─── Animation variants ─────────────────────────────────────────────────────────
const cardVariants = {
  hidden: { opacity: 0, y: 24, scale: 0.96 },
  visible: (i) => ({
    opacity: 1, y: 0, scale: 1,
    transition: { delay: i * 0.07, duration: 0.4, ease: 'easeOut' },
  }),
  exit: { opacity: 0, y: -16, scale: 0.96, transition: { duration: 0.25 } },
};

const overlayVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { duration: 0.25 } },
  exit: { opacity: 0, transition: { duration: 0.2 } },
};

const modalVariants = {
  hidden: { opacity: 0, y: 40, scale: 0.95 },
  visible: { opacity: 1, y: 0, scale: 1, transition: { duration: 0.35, ease: 'easeOut' } },
  exit: { opacity: 0, y: 20, scale: 0.95, transition: { duration: 0.2 } },
};

// =============================================================================
// MAIN COMPONENT
// =============================================================================
export default function CompanyManagement() {
  const { activeCompanyId, switchCompany, setDefaultCompany, setCompanies, companies: authCompanies } = useAuth();

  const [allCompanies, setAllCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');

  // Company modal state
  const [showModal, setShowModal] = useState(false);
  const [editingCompany, setEditingCompany] = useState(null);
  const [form, setForm] = useState({ ...EMPTY_COMPANY });
  const [formErrors, setFormErrors] = useState({});
  const [saving, setSaving] = useState(false);

  // Authority management state
  const [expandedCompanyId, setExpandedCompanyId] = useState(null);
  // Signature management state
  const [expandedSignaturesId, setExpandedSignaturesId] = useState(null);
  const [authorities, setAuthorities] = useState([]);
  const [loadingAuthorities, setLoadingAuthorities] = useState(false);
  const [showAuthorityModal, setShowAuthorityModal] = useState(false);
  const [editingAuthority, setEditingAuthority] = useState(null);
  const [authorityForm, setAuthorityForm] = useState({ ...EMPTY_AUTHORITY });
  const [authorityErrors, setAuthorityErrors] = useState({});
  const [savingAuthority, setSavingAuthority] = useState(false);
  const [authorityCompanyId, setAuthorityCompanyId] = useState(null);

  // Delete confirm
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // Switching
  const [switching, setSwitching] = useState(null);

  // ─── Fetch all companies ───────────────────────────────────────────────────
  const fetchCompanies = useCallback(async () => {
    try {
      setLoading(true);
      const res = await companyAPI.getAll();
      const data = Array.isArray(res.data) ? res.data : (res.data.companies || res.data.data || []);
      setAllCompanies(data);
      // Sync AuthContext so other pages (e.g. CommercialForm) see updated companies
      if (setCompanies) {
        const briefs = data.map(c => ({
          id: c.id, exporter_name: c.exporter_name,
          exporter_address: c.exporter_address, iec: c.iec, gstin: c.gstin,
        }));
        setCompanies(briefs);
        localStorage.setItem('companies', JSON.stringify(briefs));
      }
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to load companies');
    } finally {
      setLoading(false);
    }
  }, [setCompanies]);

  useEffect(() => {
    fetchCompanies();
  }, [fetchCompanies]);

  // ─── Fetch authorities for a company ───────────────────────────────────────
  const fetchAuthorities = useCallback(async (companyId) => {
    try {
      setLoadingAuthorities(true);
      const res = await companyAPI.getAuthorities(companyId);
      const data = Array.isArray(res.data) ? res.data : (res.data.authorities || res.data.data || []);
      setAuthorities(data);
    } catch (err) {
      toast.error('Failed to load authorities');
      setAuthorities([]);
    } finally {
      setLoadingAuthorities(false);
    }
  }, []);

  // ─── Search filter ─────────────────────────────────────────────────────────
  const filtered = allCompanies.filter((c) => {
    if (!searchQuery.trim()) return true;
    const q = searchQuery.toLowerCase();
    return (
      (c.exporter_name || '').toLowerCase().includes(q) ||
      (c.iec || '').toLowerCase().includes(q) ||
      (c.gstin || '').toLowerCase().includes(q) ||
      (c.email || '').toLowerCase().includes(q)
    );
  });

  // ─── Company form handlers ────────────────────────────────────────────────
  const openCreate = () => {
    setEditingCompany(null);
    setForm({ ...EMPTY_COMPANY });
    setFormErrors({});
    setShowModal(true);
  };

  const openEdit = (company) => {
    setEditingCompany(company);
    setForm({
      iec: company.iec || '',
      exporter_name: company.exporter_name || '',
      exporter_address: company.exporter_address || '',
      gstin: company.gstin || '',
      email: company.email || '',
      phone: company.phone || '',
      negotiation_bank: company.negotiation_bank || '',
      bank_account_no: company.bank_account_no || '',
      goods_carried_by: company.goods_carried_by || '',
      iba_code: company.iba_code || '',
      icc_bcc_code: company.icc_bcc_code || '',
      registration_date: company.registration_date
        ? new Date(company.registration_date).toISOString().split('T')[0]
        : '',
      signing_auth_type: company.signing_auth_type || 'Director',
      invoice_prefix: company.invoice_prefix || '',
      invoice_starting_number: company.invoice_starting_number || 1,
      proforma_invoice_prefix: company.proforma_invoice_prefix || 'PI-',
      proforma_invoice_starting_number: company.proforma_invoice_starting_number || 1,
      advising_bank: company.advising_bank || '',
      terms_and_conditions: company.terms_and_conditions || '',
    });
    setFormErrors({});
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingCompany(null);
    setForm({ ...EMPTY_COMPANY });
    setFormErrors({});
  };

  const validateForm = () => {
    const errors = {};
    if (!form.iec.trim()) errors.iec = 'IEC is required';
    else if (!validateIEC(form.iec.trim())) errors.iec = 'IEC must be exactly 10 alphanumeric characters';

    if (!form.exporter_name.trim()) errors.exporter_name = 'Exporter name is required';

    if (form.gstin.trim() && !validateGSTIN(form.gstin.trim())) {
      errors.gstin = 'GSTIN must be exactly 15 alphanumeric characters';
    }

    if (form.email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) {
      errors.email = 'Invalid email format';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) return;

    setSaving(true);
    try {
      const payload = { ...form, iec: form.iec.trim().toUpperCase(), gstin: form.gstin.trim().toUpperCase() };

      if (editingCompany) {
        await companyAPI.update(editingCompany.id, payload);
        toast.success('Company updated successfully');
      } else {
        await companyAPI.create(payload);
        toast.success('Company created successfully');
      }
      closeModal();
      await fetchCompanies();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to save company');
    } finally {
      setSaving(false);
    }
  };

  const handleFieldChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    if (formErrors[field]) {
      setFormErrors((prev) => { const n = { ...prev }; delete n[field]; return n; });
    }
  };

  // ─── Delete company ────────────────────────────────────────────────────────
  const handleDelete = async (companyId) => {
    setDeleting(true);
    try {
      await companyAPI.delete(companyId);
      toast.success('Company deleted');
      setDeleteConfirm(null);
      await fetchCompanies();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to delete company');
    } finally {
      setDeleting(false);
    }
  };

  // ─── Switch active company ─────────────────────────────────────────────────
  const handleSwitch = async (companyId) => {
    if (companyId === activeCompanyId) return;
    setSwitching(companyId);
    try {
      await switchCompany(companyId);
      toast.success('Switched active company');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to switch company');
    } finally {
      setSwitching(null);
    }
  };

  // ─── Set default company ─────────────────────────────────────────────────
  const [settingDefault, setSettingDefault] = useState(null);
  const handleSetDefault = async (companyId) => {
    setSettingDefault(companyId);
    try {
      await setDefaultCompany(companyId);
      toast.success('Default company updated');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to set default company');
    } finally {
      setSettingDefault(null);
    }
  };

  // ─── Authority handlers ────────────────────────────────────────────────────
  const toggleAuthorities = async (companyId) => {
    if (expandedCompanyId === companyId) {
      setExpandedCompanyId(null);
      setAuthorities([]);
      return;
    }
    setExpandedCompanyId(companyId);
    await fetchAuthorities(companyId);
  };

  const openAddAuthority = (companyId) => {
    setAuthorityCompanyId(companyId);
    setEditingAuthority(null);
    setAuthorityForm({ ...EMPTY_AUTHORITY });
    setAuthorityErrors({});
    setShowAuthorityModal(true);
  };

  const openEditAuthority = (authority, companyId) => {
    setAuthorityCompanyId(companyId);
    setEditingAuthority(authority);
    setAuthorityForm({
      authority_name: authority.authority_name || '',
      authority_type: authority.authority_type || '',
      designation: authority.designation || '',
      is_primary: authority.is_primary || false,
    });
    setAuthorityErrors({});
    setShowAuthorityModal(true);
  };

  const closeAuthorityModal = () => {
    setShowAuthorityModal(false);
    setEditingAuthority(null);
    setAuthorityForm({ ...EMPTY_AUTHORITY });
    setAuthorityErrors({});
  };

  const validateAuthority = () => {
    const errors = {};
    if (!authorityForm.authority_name.trim()) errors.authority_name = 'Name is required';
    if (!authorityForm.designation.trim()) errors.designation = 'Designation is required';
    setAuthorityErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSaveAuthority = async () => {
    if (!validateAuthority()) return;
    setSavingAuthority(true);
    try {
      if (editingAuthority) {
        await companyAPI.updateAuthority(editingAuthority.id, authorityForm);
        toast.success('Authority updated');
      } else {
        await companyAPI.addAuthority(authorityCompanyId, authorityForm);
        toast.success('Authority added');
      }
      closeAuthorityModal();
      if (expandedCompanyId) await fetchAuthorities(expandedCompanyId);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to save authority');
    } finally {
      setSavingAuthority(false);
    }
  };

  const handleDeleteAuthority = async (authorityId) => {
    try {
      await companyAPI.deleteAuthority(authorityId);
      toast.success('Authority removed');
      if (expandedCompanyId) await fetchAuthorities(expandedCompanyId);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to remove authority');
    }
  };

  const handleAuthorityFieldChange = (field, value) => {
    setAuthorityForm((prev) => ({ ...prev, [field]: value }));
    if (authorityErrors[field]) {
      setAuthorityErrors((prev) => { const n = { ...prev }; delete n[field]; return n; });
    }
  };

  // ─── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="cm-page" style={styles.page}>
      {/* Page Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
      >
        <div className="cm-header" style={styles.header}>
          <div>
            <h1 className="cm-title" style={styles.title}>Company Management</h1>
            <p className="cm-subtitle" style={styles.subtitle}>
              Manage your export companies, IEC details, and signing authorities
            </p>
          </div>
          <motion.button
            whileHover={{ scale: 1.04 }}
            whileTap={{ scale: 0.97 }}
            className="cm-primary-btn"
            style={styles.primaryBtn}
            onClick={openCreate}
          >
            <FiPlus size={18} /> Add Company
          </motion.button>
        </div>

        {/* Search */}
        <div className="cm-search" style={styles.searchWrapper}>
          <FiSearch size={18} style={styles.searchIcon} />
          <StyledInput
            placeholder="Search by name, IEC, GSTIN, or email..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={{ ...styles.searchInput, paddingLeft: 42 }}
          />
        </div>
      </motion.div>

      {/* Loading */}
      {loading && (
        <div style={{ textAlign: 'center', padding: 60 }}>
          <div style={{ ...styles.spinner, margin: '0 auto 16px', borderTopColor: COLORS.primary, border: `3px solid ${COLORS.border}`, borderTopWidth: 3, width: 36, height: 36 }} />
          <p style={{ color: COLORS.textSecondary, fontSize: 14 }}>Loading companies...</p>
        </div>
      )}

      {/* Empty State */}
      {!loading && filtered.length === 0 && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          style={styles.emptyState}
        >
          <FiBriefcase size={48} style={{ marginBottom: 16, color: COLORS.border }} />
          <h3 style={{ color: COLORS.textSecondary, fontWeight: 600, marginBottom: 8 }}>
            {searchQuery ? 'No companies match your search' : 'No companies yet'}
          </h3>
          <p style={{ color: COLORS.textMuted, fontSize: 14 }}>
            {searchQuery ? 'Try a different search term' : 'Create your first company to get started'}
          </p>
          {!searchQuery && (
            <motion.button
              whileHover={{ scale: 1.04 }}
              whileTap={{ scale: 0.97 }}
              style={{ ...styles.primaryBtn, marginTop: 20 }}
              onClick={openCreate}
            >
              <FiPlus size={18} /> Add Company
            </motion.button>
          )}
        </motion.div>
      )}

      {/* Company Cards Grid */}
      {!loading && filtered.length > 0 && (
        <motion.div className="cm-grid" style={styles.grid} layout>
          <AnimatePresence mode="popLayout">
            {filtered.map((company, idx) => {
              const isActive = company.id === activeCompanyId;
              const authCo = authCompanies.find(c => c.id === company.id);
              const isDefault = authCo?.is_default === true;
              const isExpanded = expandedCompanyId === company.id;

              return (
                <motion.div
                  key={company.id}
                  custom={idx}
                  variants={cardVariants}
                  initial="hidden"
                  animate="visible"
                  exit="exit"
                  layout
                  whileHover={{ y: -4, boxShadow: '0 12px 40px rgba(0, 0, 0, 0.1)' }}
                  className="cm-card"
                  style={{
                    ...styles.glassCard,
                    ...(isActive ? {
                      border: `2px solid ${COLORS.accent}`,
                      boxShadow: `0 8px 32px rgba(16, 185, 129, 0.15)`,
                    } : {}),
                  }}
                >
                  {/* Active indicator glow */}
                  {isActive && (
                    <div style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      height: 3,
                      background: `linear-gradient(90deg, ${COLORS.accent}, ${COLORS.primary}, ${COLORS.accent})`,
                      borderRadius: '20px 20px 0 0',
                    }} />
                  )}

                  {/* Card Header */}
                  <div className="cm-card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6, flexWrap: 'wrap' }}>
                        <h3 style={{
                          fontFamily: 'Poppins, sans-serif',
                          fontSize: 17,
                          fontWeight: 700,
                          color: COLORS.dark,
                          margin: 0,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}>
                          {company.exporter_name}
                        </h3>
                        {isActive && (
                          <span style={{ ...styles.badge, ...styles.activeBadge }}>
                            <FiCheckCircle size={11} /> Active
                          </span>
                        )}
                        {isDefault && (
                          <span style={{ ...styles.badge, background: 'rgba(245, 158, 11, 0.1)', color: '#d97706', border: '1px solid rgba(245, 158, 11, 0.3)' }}>
                            <FiStar size={11} /> Default
                          </span>
                        )}
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: COLORS.textMuted }}>
                        <FiHash size={13} />
                        <span style={{ fontWeight: 600, color: COLORS.primary }}>{company.iec || '--'}</span>
                        {company.gstin && (
                          <>
                            <span style={{ margin: '0 4px' }}>|</span>
                            <span>GSTIN: {company.gstin}</span>
                          </>
                        )}
                      </div>
                    </div>

                    {/* Action buttons */}
                    <div className="cm-card-actions" style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                      {!isActive && (
                        <motion.button
                          whileHover={{ scale: 1.1 }}
                          whileTap={{ scale: 0.9 }}
                          title="Switch to this company"
                          onClick={() => handleSwitch(company.id)}
                          disabled={switching === company.id}
                          style={{
                            ...styles.iconBtn,
                            background: COLORS.light,
                            color: COLORS.primary,
                          }}
                        >
                          {switching === company.id
                            ? <FiLoader size={15} style={{ animation: 'spin 0.8s linear infinite' }} />
                            : <FiRefreshCw size={15} />
                          }
                        </motion.button>
                      )}
                      <motion.button
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.9 }}
                        title={isDefault ? 'Default company' : 'Set as default'}
                        onClick={() => !isDefault && handleSetDefault(company.id)}
                        disabled={isDefault || settingDefault === company.id}
                        style={{
                          ...styles.iconBtn,
                          background: isDefault ? 'rgba(245, 158, 11, 0.12)' : COLORS.light,
                          color: isDefault ? '#d97706' : COLORS.textMuted,
                          cursor: isDefault ? 'default' : 'pointer',
                          opacity: isDefault ? 1 : 0.7,
                        }}
                      >
                        {settingDefault === company.id
                          ? <FiLoader size={15} style={{ animation: 'spin 0.8s linear infinite' }} />
                          : <FiStar size={15} fill={isDefault ? '#d97706' : 'none'} />
                        }
                      </motion.button>
                      <motion.button
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.9 }}
                        title="Edit company"
                        onClick={() => openEdit(company)}
                        style={{
                          ...styles.iconBtn,
                          background: COLORS.light,
                          color: COLORS.primary,
                        }}
                      >
                        <FiEdit2 size={15} />
                      </motion.button>
                      <motion.button
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.9 }}
                        title="Delete company"
                        onClick={() => setDeleteConfirm(company.id)}
                        style={{
                          ...styles.iconBtn,
                          background: COLORS.dangerBg,
                          color: COLORS.danger,
                        }}
                      >
                        <FiTrash2 size={15} />
                      </motion.button>
                    </div>
                  </div>

                  {/* Company Info Rows */}
                  {company.email && (
                    <div className="cm-info-row" style={styles.infoRow}>
                      <FiMail size={14} color={COLORS.primary} />
                      <span>{company.email}</span>
                    </div>
                  )}
                  {company.phone && (
                    <div className="cm-info-row" style={styles.infoRow}>
                      <FiPhone size={14} color={COLORS.primary} />
                      <span>{company.phone}</span>
                    </div>
                  )}
                  {company.exporter_address && (
                    <div className="cm-info-row" style={styles.infoRow}>
                      <FiMapPin size={14} color={COLORS.primary} />
                      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {company.exporter_address}
                      </span>
                    </div>
                  )}

                  {/* Additional info */}
                  <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginTop: 8 }}>
                    {company.negotiation_bank && (
                      <div style={{ fontSize: 12, color: COLORS.textMuted }}>
                        <span style={{ fontWeight: 600, color: COLORS.textSecondary }}>Bank:</span> {company.negotiation_bank}
                      </div>
                    )}
                    {company.signing_auth_type && (
                      <div style={{ fontSize: 12, color: COLORS.textMuted }}>
                        <span style={{ fontWeight: 600, color: COLORS.textSecondary }}>Auth Type:</span> {company.signing_auth_type}
                      </div>
                    )}
                    {company.registration_date && (
                      <div style={{ fontSize: 12, color: COLORS.textMuted }}>
                        <span style={{ fontWeight: 600, color: COLORS.textSecondary }}>Reg:</span> {formatDate(company.registration_date)}
                      </div>
                    )}
                  </div>

                  {/* Authorities Section */}
                  <hr style={styles.divider} />
                  <button
                    onClick={() => toggleAuthorities(company.id)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      width: '100%',
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      padding: '4px 0',
                      color: COLORS.dark,
                    }}
                  >
                    <span style={styles.sectionTitle}>
                      <FiUsers size={15} color={COLORS.primary} />
                      Managing Authorities
                    </span>
                    {isExpanded ? <FiChevronUp size={16} /> : <FiChevronDown size={16} />}
                  </button>

                  <AnimatePresence>
                    {isExpanded && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.3 }}
                        style={{ overflow: 'hidden' }}
                      >
                        <div style={{ paddingTop: 8 }}>
                          {loadingAuthorities ? (
                            <div style={{ textAlign: 'center', padding: 16 }}>
                              <div style={{ ...styles.spinner, margin: '0 auto', borderTopColor: COLORS.primary, border: `2px solid ${COLORS.border}` }} />
                            </div>
                          ) : authorities.length === 0 ? (
                            <p style={{ fontSize: 13, color: COLORS.textMuted, textAlign: 'center', padding: '12px 0' }}>
                              No authorities added yet
                            </p>
                          ) : (
                            authorities.map((auth) => (
                              <motion.div
                                key={auth.id}
                                className="cm-authority-card"
                                initial={{ opacity: 0, x: -10 }}
                                animate={{ opacity: 1, x: 0 }}
                                style={styles.authorityCard}
                              >
                                <div>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                    <span style={{ fontWeight: 600, fontSize: 14, color: COLORS.dark }}>
                                      {auth.authority_name}
                                    </span>
                                    {auth.is_primary && (
                                      <span style={{
                                        ...styles.badge,
                                        background: 'rgba(245, 158, 11, 0.12)',
                                        color: '#d97706',
                                        border: '1px solid rgba(245, 158, 11, 0.25)',
                                      }}>
                                        <FiStar size={10} /> Primary
                                      </span>
                                    )}
                                  </div>
                                  <div style={{ fontSize: 12, color: COLORS.textMuted, marginTop: 2 }}>
                                    {auth.designation}
                                    {auth.authority_type ? ` -- ${auth.authority_type}` : ''}
                                  </div>
                                </div>
                                <div className="cm-authority-actions" style={{ display: 'flex', gap: 4 }}>
                                  <motion.button
                                    whileHover={{ scale: 1.1 }}
                                    whileTap={{ scale: 0.9 }}
                                    onClick={() => openEditAuthority(auth, company.id)}
                                    style={{
                                      ...styles.iconBtn,
                                      width: 30,
                                      height: 30,
                                      background: 'rgba(16,185,129,0.1)',
                                      color: COLORS.primary,
                                    }}
                                  >
                                    <FiEdit2 size={13} />
                                  </motion.button>
                                  <motion.button
                                    whileHover={{ scale: 1.1 }}
                                    whileTap={{ scale: 0.9 }}
                                    onClick={() => handleDeleteAuthority(auth.id)}
                                    style={{
                                      ...styles.iconBtn,
                                      width: 30,
                                      height: 30,
                                      background: COLORS.dangerBg,
                                      color: COLORS.danger,
                                    }}
                                  >
                                    <FiTrash2 size={13} />
                                  </motion.button>
                                </div>
                              </motion.div>
                            ))
                          )}

                          <motion.button
                            whileHover={{ scale: 1.02 }}
                            whileTap={{ scale: 0.98 }}
                            onClick={() => openAddAuthority(company.id)}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              gap: 6,
                              width: '100%',
                              padding: '10px',
                              marginTop: 8,
                              background: COLORS.light,
                              border: `1px dashed ${COLORS.border}`,
                              borderRadius: 10,
                              color: COLORS.primary,
                              fontSize: 13,
                              fontWeight: 600,
                              cursor: 'pointer',
                            }}
                          >
                            <FiUserPlus size={14} /> Add Authority
                          </motion.button>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>

                  {/* Signatures Section */}
                  <hr style={styles.divider} />
                  <button
                    onClick={() => setExpandedSignaturesId((prev) => prev === company.id ? null : company.id)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      width: '100%',
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      padding: '4px 0',
                      color: COLORS.dark,
                    }}
                  >
                    <span style={styles.sectionTitle}>
                      <FiImage size={15} color={COLORS.primary} />
                      Signature Images
                    </span>
                    {expandedSignaturesId === company.id ? <FiChevronUp size={16} /> : <FiChevronDown size={16} />}
                  </button>

                  <AnimatePresence>
                    {expandedSignaturesId === company.id && (
                      <CompanySignatures companyId={company.id} isExpanded={true} />
                    )}
                  </AnimatePresence>
                </motion.div>
              );
            })}
          </AnimatePresence>
        </motion.div>
      )}

      {/* ═══════════════════════════════════════════════════════════════════════
          COMPANY CREATE / EDIT MODAL
          ═══════════════════════════════════════════════════════════════════════ */}
      <AnimatePresence>
        {showModal && (
          <motion.div
            className="cm-overlay"
            variants={overlayVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            style={styles.overlay}
            onClick={(e) => { if (e.target === e.currentTarget) closeModal(); }}
          >
            <motion.div
              className="cm-modal"
              variants={modalVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
              style={styles.modal}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Close button */}
              <motion.button
                whileHover={{ scale: 1.1, rotate: 90 }}
                whileTap={{ scale: 0.9 }}
                onClick={closeModal}
                style={{
                  position: 'absolute',
                  top: 16,
                  right: 16,
                  ...styles.iconBtn,
                  background: COLORS.light,
                  color: COLORS.textSecondary,
                }}
              >
                <FiX size={18} />
              </motion.button>

              <h2 className="cm-modal-title" style={styles.modalTitle}>
                {editingCompany ? 'Edit Company' : 'Create New Company'}
              </h2>

              {/* Form Grid */}
              <div className="cm-form-grid" style={styles.formGrid}>
                {/* IEC */}
                <div style={styles.fieldGroup}>
                  <label style={styles.label}>IEC Number *</label>
                  <StyledInput
                    placeholder="e.g. ABCDE12345"
                    maxLength={10}
                    value={form.iec}
                    onChange={(e) => handleFieldChange('iec', e.target.value.toUpperCase())}
                    error={formErrors.iec}
                  />
                  {formErrors.iec && <div style={styles.errorText}><FiAlertCircle size={11} /> {formErrors.iec}</div>}
                </div>

                {/* GSTIN */}
                <div style={styles.fieldGroup}>
                  <label style={styles.label}>GSTIN</label>
                  <StyledInput
                    placeholder="15-character GSTIN"
                    maxLength={15}
                    value={form.gstin}
                    onChange={(e) => handleFieldChange('gstin', e.target.value.toUpperCase())}
                    error={formErrors.gstin}
                  />
                  {formErrors.gstin && <div style={styles.errorText}><FiAlertCircle size={11} /> {formErrors.gstin}</div>}
                </div>

                {/* Exporter Name - full width */}
                <div style={{ ...styles.fieldGroup, ...styles.fullSpan }}>
                  <label style={styles.label}>Exporter Name *</label>
                  <StyledInput
                    placeholder="Company / Exporter name"
                    value={form.exporter_name}
                    onChange={(e) => handleFieldChange('exporter_name', e.target.value)}
                    error={formErrors.exporter_name}
                  />
                  {formErrors.exporter_name && <div style={styles.errorText}><FiAlertCircle size={11} /> {formErrors.exporter_name}</div>}
                </div>

                {/* Exporter Address - full width */}
                <div style={{ ...styles.fieldGroup, ...styles.fullSpan }}>
                  <label style={styles.label}>Exporter Address</label>
                  <StyledTextarea
                    placeholder="Full address of the exporter"
                    value={form.exporter_address}
                    onChange={(e) => handleFieldChange('exporter_address', e.target.value)}
                  />
                </div>

                {/* Email */}
                <div style={styles.fieldGroup}>
                  <label style={styles.label}>Email</label>
                  <StyledInput
                    type="email"
                    placeholder="company@example.com"
                    value={form.email}
                    onChange={(e) => handleFieldChange('email', e.target.value)}
                    error={formErrors.email}
                  />
                  {formErrors.email && <div style={styles.errorText}><FiAlertCircle size={11} /> {formErrors.email}</div>}
                </div>

                {/* Phone */}
                <div style={styles.fieldGroup}>
                  <label style={styles.label}>Phone</label>
                  <StyledInput
                    placeholder="Phone number"
                    value={form.phone}
                    onChange={(e) => handleFieldChange('phone', e.target.value)}
                  />
                </div>

                {/* Negotiation Bank */}
                <div style={styles.fieldGroup}>
                  <label style={styles.label}>Negotiation Bank</label>
                  <StyledInput
                    placeholder="Bank name"
                    value={form.negotiation_bank}
                    onChange={(e) => handleFieldChange('negotiation_bank', e.target.value)}
                  />
                </div>

                {/* Bank Account No */}
                <div style={styles.fieldGroup}>
                  <label style={styles.label}>Bank Account No</label>
                  <StyledInput
                    placeholder="Account number"
                    value={form.bank_account_no}
                    onChange={(e) => handleFieldChange('bank_account_no', e.target.value)}
                  />
                </div>

                {/* Goods Carried By */}
                <div style={styles.fieldGroup}>
                  <label style={styles.label}>Goods Carried By</label>
                  <StyledInput
                    placeholder="e.g. Sea, Air, Road"
                    value={form.goods_carried_by}
                    onChange={(e) => handleFieldChange('goods_carried_by', e.target.value)}
                  />
                </div>

                {/* IBA Code */}
                <div style={styles.fieldGroup}>
                  <label style={styles.label}>IBA Code</label>
                  <StyledInput
                    placeholder="IBA code"
                    value={form.iba_code}
                    onChange={(e) => handleFieldChange('iba_code', e.target.value)}
                  />
                </div>

                {/* ICC / BCC Code */}
                <div style={styles.fieldGroup}>
                  <label style={styles.label}>ICC / BCC Code</label>
                  <StyledInput
                    placeholder="ICC or BCC code"
                    value={form.icc_bcc_code}
                    onChange={(e) => handleFieldChange('icc_bcc_code', e.target.value)}
                  />
                </div>

                {/* Registration Date */}
                <div style={styles.fieldGroup}>
                  <label style={styles.label}>Registration Date</label>
                  <StyledInput
                    type="date"
                    value={form.registration_date}
                    onChange={(e) => handleFieldChange('registration_date', e.target.value)}
                  />
                </div>

                {/* Signing Auth Type - full width */}
                <div style={{ ...styles.fieldGroup, ...styles.fullSpan }}>
                  <label style={styles.label}>Signing Authority Type</label>
                  <StyledSelect
                    value={form.signing_auth_type}
                    onChange={(e) => handleFieldChange('signing_auth_type', e.target.value)}
                  >
                    {SIGNING_AUTH_TYPES.map((t) => (
                      <option key={t} value={t}>{t}</option>
                    ))}
                  </StyledSelect>
                </div>

                {/* Invoice Settings Section */}
                <div style={{ ...styles.fullSpan, borderTop: `2px solid ${COLORS.border}`, paddingTop: 16, marginTop: 8 }}>
                  <label style={{ ...styles.label, fontSize: 13, fontWeight: 700, color: COLORS.primary, marginBottom: 12, display: 'block' }}>
                    Invoice Number Settings
                  </label>
                  <p style={{ fontSize: 12, color: COLORS.textSecondary, marginBottom: 12, marginTop: -4 }}>
                    Use <code style={{ background: '#f0fdf4', padding: '1px 5px', borderRadius: 4, fontWeight: 600 }}>{'{YYYY}'}</code> for full year or <code style={{ background: '#f0fdf4', padding: '1px 5px', borderRadius: 4, fontWeight: 600 }}>{'{YY}'}</code> for 2-digit year in prefix. e.g. <code style={{ background: '#f0fdf4', padding: '1px 5px', borderRadius: 4, fontWeight: 600 }}>INV/{'{YYYY}'}/</code>
                  </p>
                  <div className="cm-form-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                    <div style={styles.fieldGroup}>
                      <label style={styles.label}>Invoice Prefix</label>
                      <StyledInput
                        value={form.invoice_prefix}
                        onChange={(e) => handleFieldChange('invoice_prefix', e.target.value)}
                        placeholder="e.g. INV/{YYYY}/"
                      />
                    </div>
                    <div style={styles.fieldGroup}>
                      <label style={styles.label}>Starting Number</label>
                      <StyledInput
                        type="number"
                        min="1"
                        value={form.invoice_starting_number}
                        onChange={(e) => handleFieldChange('invoice_starting_number', parseInt(e.target.value) || 1)}
                        placeholder="1"
                      />
                    </div>
                  </div>
                  <p style={{ fontSize: 11, color: COLORS.textSecondary, marginTop: 8 }}>
                    Preview: <strong style={{ color: COLORS.primary }}>
                      {(form.invoice_prefix || 'INV-').replace(/\{YYYY\}/g, new Date().getFullYear()).replace(/\{YY\}/g, String(new Date().getFullYear() % 100).padStart(2, '0'))}{String(form.invoice_starting_number || 1).padStart(3, '0')}
                    </strong>
                  </p>
                </div>

                {/* Proforma Invoice Settings Section */}
                <div style={{ ...styles.fullSpan, borderTop: `2px solid ${COLORS.border}`, paddingTop: 16, marginTop: 8 }}>
                  <label style={{ ...styles.label, fontSize: 13, fontWeight: 700, color: COLORS.warning, marginBottom: 12, display: 'block' }}>
                    Proforma Invoice Number Settings
                  </label>
                  <p style={{ fontSize: 12, color: COLORS.textSecondary, marginBottom: 12, marginTop: -4 }}>
                    Separate numbering series for Proforma Invoices. Use <code style={{ background: '#fef3c7', padding: '1px 5px', borderRadius: 4, fontWeight: 600 }}>{'{YYYY}'}</code> for full year or <code style={{ background: '#fef3c7', padding: '1px 5px', borderRadius: 4, fontWeight: 600 }}>{'{YY}'}</code> for 2-digit year.
                  </p>
                  <div className="cm-form-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                    <div style={styles.fieldGroup}>
                      <label style={styles.label}>Proforma Invoice Prefix</label>
                      <StyledInput
                        value={form.proforma_invoice_prefix}
                        onChange={(e) => handleFieldChange('proforma_invoice_prefix', e.target.value)}
                        placeholder="e.g. PI-{YYYY}-"
                      />
                    </div>
                    <div style={styles.fieldGroup}>
                      <label style={styles.label}>Starting Number</label>
                      <StyledInput
                        type="number"
                        min="1"
                        value={form.proforma_invoice_starting_number}
                        onChange={(e) => handleFieldChange('proforma_invoice_starting_number', parseInt(e.target.value) || 1)}
                        placeholder="1"
                      />
                    </div>
                  </div>
                  <p style={{ fontSize: 11, color: COLORS.textSecondary, marginTop: 8 }}>
                    Preview: <strong style={{ color: '#92400e' }}>
                      {(form.proforma_invoice_prefix || 'PI-').replace(/\{YYYY\}/g, new Date().getFullYear()).replace(/\{YY\}/g, String(new Date().getFullYear() % 100).padStart(2, '0'))}{String(form.proforma_invoice_starting_number || 1).padStart(3, '0')}
                    </strong>
                  </p>
                </div>

                {/* Advising Bank & Terms Section */}
                <div style={{ ...styles.fullSpan, borderTop: `2px solid ${COLORS.border}`, paddingTop: 16, marginTop: 8 }}>
                  <label style={{ ...styles.label, fontSize: 13, fontWeight: 700, color: COLORS.primary, marginBottom: 12, display: 'block' }}>
                    Advising Bank & Terms
                  </label>
                  <div style={styles.fieldGroup}>
                    <label style={styles.label}>Advising Bank</label>
                    <StyledInput
                      value={form.advising_bank}
                      onChange={(e) => handleFieldChange('advising_bank', e.target.value)}
                      placeholder="e.g. ICICI BANK LIMITED, KOLKATA, INDIA"
                    />
                  </div>
                  <div style={{ ...styles.fieldGroup, marginTop: 12 }}>
                    <label style={styles.label}>Terms & Conditions</label>
                    <textarea
                      value={form.terms_and_conditions}
                      onChange={(e) => handleFieldChange('terms_and_conditions', e.target.value)}
                      placeholder="Enter default terms and conditions for proforma invoices..."
                      rows={5}
                      style={{
                        width: '100%',
                        padding: '10px 14px',
                        border: `2px solid ${COLORS.border}`,
                        borderRadius: 10,
                        fontSize: 14,
                        outline: 'none',
                        background: '#f0fdf4',
                        fontFamily: 'Inter, sans-serif',
                        resize: 'vertical',
                        minHeight: 80,
                        boxSizing: 'border-box',
                      }}
                    />
                  </div>
                </div>
              </div>

              {/* Buttons */}
              <div className="cm-btn-row" style={styles.btnRow}>
                <motion.button
                  whileHover={{ scale: 1.03 }}
                  whileTap={{ scale: 0.97 }}
                  onClick={closeModal}
                  style={styles.cancelBtn}
                >
                  Cancel
                </motion.button>
                <motion.button
                  whileHover={{ scale: 1.03 }}
                  whileTap={{ scale: 0.97 }}
                  onClick={handleSave}
                  disabled={saving}
                  style={{ ...styles.saveBtn, opacity: saving ? 0.7 : 1, cursor: saving ? 'not-allowed' : 'pointer' }}
                >
                  {saving ? (
                    <>
                      <div style={styles.spinner} />
                      Saving...
                    </>
                  ) : (
                    <>
                      <FiCheck size={16} />
                      {editingCompany ? 'Update Company' : 'Create Company'}
                    </>
                  )}
                </motion.button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ═══════════════════════════════════════════════════════════════════════
          AUTHORITY CREATE / EDIT MODAL
          ═══════════════════════════════════════════════════════════════════════ */}
      <AnimatePresence>
        {showAuthorityModal && (
          <motion.div
            className="cm-overlay"
            variants={overlayVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            style={styles.overlay}
            onClick={(e) => { if (e.target === e.currentTarget) closeAuthorityModal(); }}
          >
            <motion.div
              className="cm-modal"
              variants={modalVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
              style={{ ...styles.modal, maxWidth: 480 }}
              onClick={(e) => e.stopPropagation()}
            >
              <motion.button
                whileHover={{ scale: 1.1, rotate: 90 }}
                whileTap={{ scale: 0.9 }}
                onClick={closeAuthorityModal}
                style={{
                  position: 'absolute',
                  top: 16,
                  right: 16,
                  ...styles.iconBtn,
                  background: COLORS.light,
                  color: COLORS.textSecondary,
                }}
              >
                <FiX size={18} />
              </motion.button>

              <h2 className="cm-modal-title" style={styles.modalTitle}>
                {editingAuthority ? 'Edit Authority' : 'Add Authority'}
              </h2>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                {/* Authority Name */}
                <div style={styles.fieldGroup}>
                  <label style={styles.label}>Authority Name *</label>
                  <StyledInput
                    placeholder="Full name"
                    value={authorityForm.authority_name}
                    onChange={(e) => handleAuthorityFieldChange('authority_name', e.target.value)}
                    error={authorityErrors.authority_name}
                  />
                  {authorityErrors.authority_name && (
                    <div style={styles.errorText}><FiAlertCircle size={11} /> {authorityErrors.authority_name}</div>
                  )}
                </div>

                {/* Authority Type */}
                <div style={styles.fieldGroup}>
                  <label style={styles.label}>Authority Type</label>
                  <StyledInput
                    placeholder="e.g. Signatory, Authorized Representative"
                    value={authorityForm.authority_type}
                    onChange={(e) => handleAuthorityFieldChange('authority_type', e.target.value)}
                  />
                </div>

                {/* Designation */}
                <div style={styles.fieldGroup}>
                  <label style={styles.label}>Designation *</label>
                  <StyledInput
                    placeholder="e.g. Director, Managing Partner"
                    value={authorityForm.designation}
                    onChange={(e) => handleAuthorityFieldChange('designation', e.target.value)}
                    error={authorityErrors.designation}
                  />
                  {authorityErrors.designation && (
                    <div style={styles.errorText}><FiAlertCircle size={11} /> {authorityErrors.designation}</div>
                  )}
                </div>

                {/* Is Primary */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <input
                    type="checkbox"
                    id="is_primary_auth"
                    checked={authorityForm.is_primary}
                    onChange={(e) => handleAuthorityFieldChange('is_primary', e.target.checked)}
                    style={styles.checkbox}
                  />
                  <label htmlFor="is_primary_auth" style={{ fontSize: 14, color: COLORS.dark, fontWeight: 500, cursor: 'pointer' }}>
                    Primary signing authority
                  </label>
                </div>
              </div>

              {/* Buttons */}
              <div className="cm-btn-row" style={styles.btnRow}>
                <motion.button
                  whileHover={{ scale: 1.03 }}
                  whileTap={{ scale: 0.97 }}
                  onClick={closeAuthorityModal}
                  style={styles.cancelBtn}
                >
                  Cancel
                </motion.button>
                <motion.button
                  whileHover={{ scale: 1.03 }}
                  whileTap={{ scale: 0.97 }}
                  onClick={handleSaveAuthority}
                  disabled={savingAuthority}
                  style={{ ...styles.saveBtn, opacity: savingAuthority ? 0.7 : 1, cursor: savingAuthority ? 'not-allowed' : 'pointer' }}
                >
                  {savingAuthority ? (
                    <>
                      <div style={styles.spinner} />
                      Saving...
                    </>
                  ) : (
                    <>
                      <FiCheck size={16} />
                      {editingAuthority ? 'Update' : 'Add Authority'}
                    </>
                  )}
                </motion.button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ═══════════════════════════════════════════════════════════════════════
          DELETE CONFIRMATION MODAL
          ═══════════════════════════════════════════════════════════════════════ */}
      <AnimatePresence>
        {deleteConfirm && (
          <motion.div
            className="cm-overlay"
            variants={overlayVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            style={styles.overlay}
            onClick={(e) => { if (e.target === e.currentTarget) setDeleteConfirm(null); }}
          >
            <motion.div
              className="cm-modal"
              variants={modalVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
              style={{ ...styles.modal, maxWidth: 420, textAlign: 'center' }}
              onClick={(e) => e.stopPropagation()}
            >
              <div style={{
                width: 56,
                height: 56,
                borderRadius: '50%',
                background: COLORS.dangerBg,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 20px',
              }}>
                <FiTrash2 size={24} color={COLORS.danger} />
              </div>
              <h3 style={{
                fontFamily: 'Poppins, sans-serif',
                fontSize: 20,
                fontWeight: 700,
                color: COLORS.dark,
                margin: '0 0 8px',
              }}>
                Delete Company?
              </h3>
              <p style={{ color: COLORS.textSecondary, fontSize: 14, marginBottom: 28, lineHeight: 1.5 }}>
                This action will soft-delete the company. Associated data will be preserved but the company will no longer appear in active listings.
              </p>
              <div className="cm-btn-row" style={{ display: 'flex', gap: 12, justifyContent: 'center' }}>
                <motion.button
                  whileHover={{ scale: 1.03 }}
                  whileTap={{ scale: 0.97 }}
                  onClick={() => setDeleteConfirm(null)}
                  style={styles.cancelBtn}
                  disabled={deleting}
                >
                  Cancel
                </motion.button>
                <motion.button
                  whileHover={{ scale: 1.03 }}
                  whileTap={{ scale: 0.97 }}
                  onClick={() => handleDelete(deleteConfirm)}
                  disabled={deleting}
                  style={{
                    ...styles.dangerBtn,
                    padding: '11px 24px',
                    fontSize: 14,
                    fontWeight: 700,
                    borderRadius: 10,
                    opacity: deleting ? 0.7 : 1,
                    cursor: deleting ? 'not-allowed' : 'pointer',
                  }}
                >
                  {deleting ? (
                    <>
                      <div style={{ ...styles.spinner, borderTopColor: COLORS.danger, border: `2px solid ${COLORS.dangerBorder}` }} />
                      Deleting...
                    </>
                  ) : (
                    <>
                      <FiTrash2 size={15} />
                      Delete Company
                    </>
                  )}
                </motion.button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}